import { useEffect, useMemo, useState } from 'react';
import { FileText, X, AlertTriangle, Plus, Trash2, Loader } from 'lucide-react';

interface LineItem {
  item_type: 'labor' | 'parts';
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface InvoiceData {
  serviceProvider?: string;
  providerEmail?: string;
  providerPhone?: string;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  serviceDate?: string;
  unitNumber?: string;
  vin?: string;
  odometer?: string;
  laborCost?: string;
  partsCost?: string;
  cost?: string;
  lineItems?: LineItem[];
}

interface InvoicePreviewProps {
  data: InvoiceData;
  // Called with the (possibly edited) values when the user clicks Continue.
  onConfirm: (edited: InvoiceData) => void;
  // Called to dismiss without importing.
  onClose: () => void;
  fileUrl?: string | null;
  fileType?: 'pdf' | 'image';
  fileName?: string;
}

const inputClass =
  'w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';

export function InvoicePreview({ data, onConfirm, onClose, fileUrl, fileType, fileName }: InvoicePreviewProps) {
  // Editable working copy of the extracted data.
  const [edited, setEdited] = useState<InvoiceData>(() => ({
    ...data,
    lineItems: data.lineItems ? data.lineItems.map(i => ({ ...i })) : [],
  }));

  // Rendered PDF pages (data URLs). pdf.js is far more reliable than a blob
  // <iframe>, and matches how the rest of the app renders PDFs.
  const [pdfPages, setPdfPages] = useState<string[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);

  useEffect(() => {
    if (!fileUrl || fileType !== 'pdf') return;
    let cancelled = false;
    setPdfLoading(true);
    setPdfError(false);
    setPdfPages([]);

    (async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc =
          'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.449/build/pdf.worker.min.mjs';
        const pdf = await pdfjsLib.getDocument({
          url: fileUrl,
          cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.449/cmaps/',
          cMapPacked: true,
        }).promise;

        const pages: string[] = [];
        const maxPages = Math.min(pdf.numPages, 15);
        for (let i = 1; i <= maxPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          if (context) {
            await page.render({ canvasContext: context, viewport } as any).promise;
            pages.push(canvas.toDataURL('image/jpeg', 0.85));
          }
        }
        if (!cancelled) {
          setPdfPages(pages);
          setPdfLoading(false);
        }
      } catch (e) {
        console.error('PDF preview render failed:', e);
        if (!cancelled) {
          setPdfError(true);
          setPdfLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fileUrl, fileType]);

  function setField<K extends keyof InvoiceData>(field: K, value: InvoiceData[K]) {
    setEdited(prev => ({ ...prev, [field]: value }));
  }

  function updateLineItem(index: number, patch: Partial<LineItem>) {
    setEdited(prev => {
      const items = [...(prev.lineItems || [])];
      items[index] = { ...items[index], ...patch };
      return { ...prev, lineItems: items };
    });
  }

  function removeLineItem(index: number) {
    setEdited(prev => ({
      ...prev,
      lineItems: (prev.lineItems || []).filter((_, i) => i !== index),
    }));
  }

  function addLineItem(item_type: 'labor' | 'parts') {
    setEdited(prev => ({
      ...prev,
      lineItems: [...(prev.lineItems || []), { item_type, description: '', quantity: 1, rate: 0, amount: 0 }],
    }));
  }

  const lineItems = edited.lineItems || [];

  // Quick at-a-glance total of the line items so the user can sanity-check it
  // against the parsed total they're editing below.
  const lineItemTotal = useMemo(
    () => lineItems.reduce((sum, i) => sum + (Number(i.amount) || 0), 0),
    [lineItems]
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Review &amp; Edit Extracted Data</h2>
              <p className="text-xs text-gray-500">Fix anything the parser misread — your edits import into the form.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Cancel">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-8">
          {/* Uploaded file preview — verify the extraction against the original */}
          {fileUrl && (
            <div className="mb-6">
              {fileType === 'image' && (
                <div className="mb-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    <span className="font-semibold">Image uploaded — please double-check everything.</span>{' '}
                    Extracting from a photo can be less accurate than a PDF. Compare the preview below with the fields and fix anything that's off before continuing.
                  </p>
                </div>
              )}
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Uploaded file{fileName ? `: ${fileName}` : ''}
              </p>
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-100">
                {fileType === 'image' ? (
                  <img src={fileUrl} alt="Uploaded document" className="max-h-[460px] w-auto mx-auto object-contain" />
                ) : pdfLoading ? (
                  <div className="flex items-center justify-center gap-2 h-[300px] text-gray-500 text-sm">
                    <Loader className="w-4 h-4 animate-spin" /> Rendering PDF preview…
                  </div>
                ) : pdfError ? (
                  <div className="p-6 text-center text-sm text-gray-600">
                    Couldn't render the PDF here.{' '}
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                      Open it in a new tab
                    </a>
                  </div>
                ) : (
                  <div className="max-h-[460px] overflow-y-auto p-2 space-y-2">
                    {pdfPages.map((src, i) => (
                      <img key={i} src={src} alt={`Page ${i + 1}`} className="w-full shadow-sm" />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Service Provider */}
          <div className="border-b-2 border-gray-300 pb-6 mb-6">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Service Provider</label>
            <input
              className={`${inputClass} text-lg font-bold !py-2 mb-3`}
              value={edited.serviceProvider || ''}
              onChange={e => setField('serviceProvider', e.target.value)}
              placeholder="Service provider / shop name"
            />
            <div className="grid grid-cols-2 gap-3">
              <input className={inputClass} value={edited.street || ''} onChange={e => setField('street', e.target.value)} placeholder="Street address" />
              <input className={inputClass} value={edited.city || ''} onChange={e => setField('city', e.target.value)} placeholder="City" />
              <input className={inputClass} value={edited.state || ''} onChange={e => setField('state', e.target.value)} placeholder="State" />
              <input className={inputClass} value={edited.zipCode || ''} onChange={e => setField('zipCode', e.target.value)} placeholder="Zip" />
              <input className={inputClass} value={edited.providerEmail || ''} onChange={e => setField('providerEmail', e.target.value)} placeholder="Email" />
              <input className={inputClass} value={edited.providerPhone || ''} onChange={e => setField('providerPhone', e.target.value)} placeholder="Phone" />
            </div>
          </div>

          {/* Vehicle + date */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Service Date</label>
              <input className={inputClass} value={edited.serviceDate || ''} onChange={e => setField('serviceDate', e.target.value)} placeholder="YYYY-MM-DD" />
            </div>
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Unit #</label>
                <input className={inputClass} value={edited.unitNumber || ''} onChange={e => setField('unitNumber', e.target.value)} placeholder="Truck/Trailer unit #" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input className={inputClass} value={edited.vin || ''} onChange={e => setField('vin', e.target.value)} placeholder="VIN" />
                <input className={inputClass} value={edited.odometer || ''} onChange={e => setField('odometer', e.target.value)} placeholder="Odometer" />
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-lg">Service Details</h3>
              <div className="text-xs text-gray-500">Line-item total: ${lineItemTotal.toFixed(2)}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-gray-300 text-xs text-gray-600">
                    <th className="text-left py-2 px-2 font-semibold w-24">Type</th>
                    <th className="text-left py-2 px-2 font-semibold">Description</th>
                    <th className="text-right py-2 px-2 font-semibold w-16">Qty</th>
                    <th className="text-right py-2 px-2 font-semibold w-24">Rate</th>
                    <th className="text-right py-2 px-2 font-semibold w-24">Amount</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-200">
                      <td className="py-1 px-1">
                        <select
                          className={inputClass}
                          value={item.item_type}
                          onChange={e => updateLineItem(idx, { item_type: e.target.value as 'labor' | 'parts' })}
                        >
                          <option value="labor">Labor</option>
                          <option value="parts">Parts</option>
                        </select>
                      </td>
                      <td className="py-1 px-1">
                        <input className={inputClass} value={item.description} onChange={e => updateLineItem(idx, { description: e.target.value })} />
                      </td>
                      <td className="py-1 px-1">
                        <input type="number" step="0.01" className={`${inputClass} text-right`} value={item.quantity} onChange={e => updateLineItem(idx, { quantity: parseFloat(e.target.value) || 0 })} />
                      </td>
                      <td className="py-1 px-1">
                        <input type="number" step="0.01" className={`${inputClass} text-right`} value={item.rate} onChange={e => updateLineItem(idx, { rate: parseFloat(e.target.value) || 0 })} />
                      </td>
                      <td className="py-1 px-1">
                        <input type="number" step="0.01" className={`${inputClass} text-right`} value={item.amount} onChange={e => updateLineItem(idx, { amount: parseFloat(e.target.value) || 0 })} />
                      </td>
                      <td className="py-1 px-1 text-center">
                        <button onClick={() => removeLineItem(idx)} className="text-gray-400 hover:text-red-600" title="Remove line">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {lineItems.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-3 px-2 text-sm text-gray-400 text-center">No line items extracted — add them below if needed.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => addLineItem('labor')} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
                <Plus className="w-4 h-4" /> Add labor
              </button>
              <button onClick={() => addLineItem('parts')} className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700 font-medium">
                <Plus className="w-4 h-4" /> Add parts
              </button>
            </div>
          </div>

          {/* Totals (editable) */}
          <div className="border-t-2 border-gray-300 pt-6">
            <div className="max-w-sm ml-auto space-y-2">
              <div className="flex justify-between items-center text-gray-700">
                <span className="font-medium">Labor:</span>
                <div className="flex items-center"><span className="text-gray-500 mr-1">$</span>
                  <input className={`${inputClass} w-28 text-right`} value={edited.laborCost || ''} onChange={e => setField('laborCost', e.target.value)} placeholder="0.00" />
                </div>
              </div>
              <div className="flex justify-between items-center text-gray-700">
                <span className="font-medium">Parts:</span>
                <div className="flex items-center"><span className="text-gray-500 mr-1">$</span>
                  <input className={`${inputClass} w-28 text-right`} value={edited.partsCost || ''} onChange={e => setField('partsCost', e.target.value)} placeholder="0.00" />
                </div>
              </div>
              <div className="flex justify-between items-center text-gray-900 pt-2 border-t border-gray-300 font-bold">
                <span>Total:</span>
                <div className="flex items-center"><span className="text-gray-600 mr-1">$</span>
                  <input className={`${inputClass} w-28 text-right font-bold`} value={edited.cost || ''} onChange={e => setField('cost', e.target.value)} placeholder="0.00" />
                </div>
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end gap-3">
            <button onClick={onClose} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
              Cancel
            </button>
            <button
              onClick={() => onConfirm(edited)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Continue to Form
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
